import React, { useMemo } from 'react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { usePreviewSettings } from '../templateSettings';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';
  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  if (yearOnly) return date.getFullYear().toString();
  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }
  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);
  switch (dateFormat) {
    case '%b %y': return `${monthShort} ${yearShort}`;
    case '%B %Y': return `${monthLong} ${year}`;
    case '%b %Y': return `${monthShort} ${year}`;
    case '%m/%Y': return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y': return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y': return year.toString();
    default: return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string, yearOnly?: boolean, hideDate?: boolean): string => {
  if (hideDate) return '';
  if (!startDate) return '';
  const start = formatDateWithFormat(startDate, dateFormat, yearOnly);
  if (isCurrent) return `${start} - Present`;
  if (!endDate) return `${start} - Present`;
  const end = formatDateWithFormat(endDate, dateFormat, yearOnly);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

interface EditableLineProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableLine: React.FC<EditableLineProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <li
      className={`resume-editable-line ${isActive ? 'resume-editable-line--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </li>
  );
};

export const CedarTemplate: React.FC<ResumePreviewProps> = ({ data, settings, structures = [], activeField, onFieldClick }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    categorySkills,
    links
  } = data;

  const dateFormat = settings?.date_format;
  const { rootStyle } = usePreviewSettings(settings);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
    'TYPE_LINK_BLOCKS': 'links',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) map[sectionId] = s.structure_name;
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  return (
    <div className="resume-preview resume-cedar" style={rootStyle}>
      <div className="resume-cedar__header">
        <div className="resume-cedar__header-left">
          <h1 className="resume-cedar__name">
            <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.firstName}
            </EditableField>
            {' '}
            <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.lastName}
            </EditableField>
          </h1>
          {targetJobTitle && (
            <p className="resume-cedar__job-title">
              <EditableField fieldName="targetJobTitle" activeField={activeField} onFieldClick={onFieldClick}>
                {targetJobTitle}
              </EditableField>
            </p>
          )}
        </div>
        <div className="resume-cedar__header-right">
          {personalInfo.email && (
            <p>
              <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.email}
              </EditableField>
            </p>
          )}
          {links?.find(l => l.type === 'linkedin')?.url && (
            <p>
              <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                {links.find(l => l.type === 'linkedin')?.url}
              </EditableField>
            </p>
          )}
          {personalInfo.location && (
            <p>
              <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.location}
              </EditableField>
            </p>
          )}
          {personalInfo.phone && (
            <p>
              <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.phone}
              </EditableField>
            </p>
          )}
        </div>
      </div>

      {professionalSummary && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('about', 'Professional Summary')}</span>
          </h2>
          <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
            <ul className="resume-cedar__list">
              {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
              ))}
            </ul>
          </EditableBlock>
        </div>
      )}

      {workExperiences && workExperiences.length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('experience', 'Experience')}</span>
          </h2>
          {workExperiences.map((exp: WorkExperience) => (
            <EditableBlock
              key={exp.uid}
              fieldName={`experience.${exp.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-cedar__entry"
            >
              <div className="resume-cedar__entry-header">
                <div className="resume-cedar__entry-title">
                  <EditableField
                    fieldName={`experience.${exp.uid}.job_title`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-role"
                  >
                    {exp.job_title}
                  </EditableField>
                  <span className="resume-cedar__entry-separator"> · </span>
                  <EditableField
                    fieldName={`experience.${exp.uid}.employer_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-company"
                  >
                    {exp.employer_name}
                  </EditableField>
                </div>
                <span className="resume-cedar__entry-date">
                  <EditableField
                    fieldName={`experience.${exp.uid}.start_date`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                  </EditableField>
                </span>
              </div>
              {exp.location && (
                <EditableField
                  fieldName={`experience.${exp.uid}.location`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-subtitle">{exp.location}</p>
                </EditableField>
              )}
              {exp.description && (
                <EditableField
                  fieldName={`experience.${exp.uid}.description`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-description">· {exp.description}</p>
                </EditableField>
              )}
              {exp.achievements && exp.achievements.length > 0 && (
                <ul className="resume-cedar__list">
                  {exp.achievements.map((achievement, idx) => (
                    <EditableLine
                      key={idx}
                      fieldName={`experience.${exp.uid}.achievement.${idx}`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {achievement}
                    </EditableLine>
                  ))}
                </ul>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {volunteerExperiences && volunteerExperiences.length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('volunteer', 'Volunteer Experience')}</span>
          </h2>
          {volunteerExperiences.map((exp: WorkExperience) => (
            <EditableBlock
              key={exp.uid}
              fieldName={`volunteer.${exp.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-cedar__entry"
            >
              <div className="resume-cedar__entry-header">
                <div className="resume-cedar__entry-title">
                  <EditableField
                    fieldName={`volunteer.${exp.uid}.job_title`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-role"
                  >
                    {exp.job_title}
                  </EditableField>
                  <span className="resume-cedar__entry-separator"> · </span>
                  <EditableField
                    fieldName={`volunteer.${exp.uid}.employer_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-company"
                  >
                    {exp.employer_name}
                  </EditableField>
                </div>
                <span className="resume-cedar__entry-date">
                  <EditableField
                    fieldName={`volunteer.${exp.uid}.start_date`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                  </EditableField>
                </span>
              </div>
              {exp.achievements && exp.achievements.length > 0 && (
                <ul className="resume-cedar__list">
                  {exp.achievements.map((achievement, idx) => (
                    <EditableLine
                      key={idx}
                      fieldName={`volunteer.${exp.uid}.achievement.${idx}`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {achievement}
                    </EditableLine>
                  ))}
                </ul>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {educations && educations.length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('education', 'Education')}</span>
          </h2>
          {educations.map((edu: Education) => (
            <EditableBlock
              key={edu.uid}
              fieldName={`education.${edu.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-cedar__entry"
            >
              <div className="resume-cedar__entry-header">
                <div className="resume-cedar__entry-title">
                  <EditableField
                    fieldName={`education.${edu.uid}.institution`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-role"
                  >
                    {edu.institution}
                  </EditableField>
                  {edu.degree && (
                    <>
                      <span className="resume-cedar__entry-separator"> | </span>
                      <EditableField
                        fieldName={`education.${edu.uid}.degree`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {edu.degree}
                      </EditableField>
                    </>
                  )}
                </div>
                {edu.graduation_date && (
                  <span className="resume-cedar__entry-date">
                    <EditableField
                      fieldName={`education.${edu.uid}.graduation_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {formatDateWithFormat(edu.graduation_date, dateFormat)}
                    </EditableField>
                  </span>
                )}
              </div>
              {edu.field_of_study && (
                <EditableField
                  fieldName={`education.${edu.uid}.field_of_study`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-subtitle">{edu.field_of_study}</p>
                </EditableField>
              )}
              {edu.grade && (
                <EditableField
                  fieldName={`education.${edu.uid}.grade`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-subtitle">GPA: {edu.grade}</p>
                </EditableField>
              )}
              {edu.description && (
                <EditableField
                  fieldName={`education.${edu.uid}.description`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-description">{edu.description}</p>
                </EditableField>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {certifications && certifications.length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('certifications', 'Certifications')}</span>
          </h2>
          {certifications.map((cert: Certification) => (
            <EditableBlock
              key={cert.uid}
              fieldName={`certification.${cert.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-cedar__entry"
            >
              <div className="resume-cedar__entry-header">
                <div className="resume-cedar__entry-title">
                  <EditableField
                    fieldName={`certification.${cert.uid}.certification_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-role"
                  >
                    {cert.certification_name}
                  </EditableField>
                  {cert.organization_name && (
                    <>
                      <span className="resume-cedar__entry-separator"> | </span>
                      <EditableField
                        fieldName={`certification.${cert.uid}.organization_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {cert.organization_name}
                      </EditableField>
                    </>
                  )}
                  {cert.expires_at && (
                    <>
                      <span className="resume-cedar__entry-separator"> | </span>
                      <span>Expires: {formatDateWithFormat(cert.expires_at, dateFormat)}</span>
                    </>
                  )}
                  {cert.credential_id && (
                    <>
                      <span className="resume-cedar__entry-separator"> | </span>
                      <EditableField
                        fieldName={`certification.${cert.uid}.credential_id`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        ID: {cert.credential_id}
                      </EditableField>
                    </>
                  )}
                </div>
                {cert.issued_at && (
                  <span className="resume-cedar__entry-date">
                    <EditableField
                      fieldName={`certification.${cert.uid}.issued_at`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {formatDateWithFormat(cert.issued_at, dateFormat)}
                    </EditableField>
                  </span>
                )}
              </div>
              {cert.description && (
                <EditableField
                  fieldName={`certification.${cert.uid}.description`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-subtitle">{cert.description}</p>
                </EditableField>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {links && links.filter(l => l.type !== 'linkedin').length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('links', 'Links')}</span>
          </h2>
          <div className="resume-cedar__links">
            {links.filter(l => l.type !== 'linkedin').map((link, idx) => (
              <EditableBlock
                key={idx}
                fieldName={`link.${idx}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-cedar__link-item"
              >
                <span className="resume-cedar__link-type">{link.type}</span>
                <span className="resume-cedar__link-url">{link.url}</span>
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0) && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('skills', 'Skills')}</span>
          </h2>
          <div className="resume-cedar__skills">
            {Object.entries(categorySkills).map(([category, skills]) => (
              skills && skills.length > 0 && (
                <EditableField
                  key={category}
                  fieldName={`skills.${category}`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__skill-line">
                    {category !== 'General skills' && <strong>{category}: </strong>}
                    {skills.join(' · ')}
                  </p>
                </EditableField>
              )
            ))}
          </div>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="resume-cedar__section">
          <h2 className="resume-cedar__section-title">
            <span>{getSectionTitle('projects', 'Projects')}</span>
          </h2>
          {projects.map((project: Project) => (
            <EditableBlock
              key={project.uid}
              fieldName={`project.${project.uid}`}
              activeField={activeField}
              onFieldClick={onFieldClick}
              className="resume-cedar__entry"
            >
              <div className="resume-cedar__entry-header">
                <div className="resume-cedar__entry-title">
                  <EditableField
                    fieldName={`project.${project.uid}.project_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-cedar__entry-role"
                  >
                    {project.project_name}
                  </EditableField>
                  {project.url && (
                    <>
                      <span className="resume-cedar__entry-separator"> · </span>
                      <EditableField
                        fieldName={`project.${project.uid}.url`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {project.url}
                      </EditableField>
                    </>
                  )}
                </div>
              </div>
              {project.description && (
                <EditableField
                  fieldName={`project.${project.uid}.description`}
                  activeField={activeField}
                  onFieldClick={onFieldClick}
                >
                  <p className="resume-cedar__entry-subtitle">{project.description}</p>
                </EditableField>
              )}
            </EditableBlock>
          ))}
        </div>
      )}
    </div>
  );
};
